/**
 * Single source of truth for coach- and Ops-facing recurring-series summaries.
 *
 * MEN-4122 fixed the reschedule dialog to show a cadence ("Every other Tuesday
 * at 11:30am from ... to ...") instead of the generic scope label. That fix
 * lived as a private helper inside CoachRescheduleSession.tsx, imported by
 * nothing, so when the cancel dialog needed the same summary it grew its own
 * generic fallback and MEN-4306 reported the identical defect against Ops
 * cancel. The helper is shared here so the next dialog cannot regress it the
 * same way -- a helper callers must remember to reimplement is how this
 * recurs.
 */

export interface SeriesCadence {
  /** Local calendar date (YYYY-MM-DD) of the last occurrence, when known. */
  endsOn?: string | null;
  /** Weeks between occurrences. Without it there is no cadence to describe. */
  intervalWeeks?: number | null;
  /**
   * How many occurrences the window covers, when known. Callers that already
   * print their own count leave this unset -- the cancel dialog does, and
   * `cadenceFromOccurrences` deliberately never returns it -- so setting it
   * there would print the count twice in the same line.
   */
  occurrenceCount?: number | null;
  /** Local calendar date (YYYY-MM-DD) of the first occurrence, when known. */
  startsOn?: string | null;
}

const formatTime = (iso: string, timezone: string) =>
  new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone,
  }).format(new Date(iso));

const formatWeekdayTime = (iso: string, timezone: string) =>
  `${new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    weekday: 'long',
  }).format(new Date(iso))} at ${new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone,
  }).format(new Date(iso))}`;

const formatDate = (date: string) =>
  new Intl.DateTimeFormat('en-US', {
    dateStyle: 'long',
    timeZone: 'UTC',
  }).format(new Date(`${date}T00:00:00Z`));

/**
 * MEN-4306: what the summary knows is not all-or-nothing. A series whose
 * occurrences disagree on a cadence still has a window and a count, and both
 * answer the question the summary is there to answer -- what am I about to
 * cancel. Returning nothing because the frequency could not be derived threw
 * away the two facts the server did assert and left the caller printing a
 * label that says less than either.
 *
 * The count is required rather than optional because there is no reviewed
 * sentence for a bare window, and an unreviewed one would render immediately
 * above a destructive confirmation.
 */
const windowSummary = ({
  endsOn,
  occurrenceCount,
  startsOn,
}: SeriesCadence): string | null => {
  if (!startsOn || !endsOn || !occurrenceCount || occurrenceCount < 1)
    return null;
  const sessions = occurrenceCount === 1 ? 'session' : 'sessions';
  return `${occurrenceCount} ${sessions} from ${formatDate(
    startsOn,
  )} to ${formatDate(endsOn)}.`;
};

/**
 * MEN-4122: the date range is optional, the cadence is not. The browser
 * scheduling context carries no series end date -- only the Ops contract has
 * `affectedEndAt` -- so requiring `endsOn` made every coach-side series fall
 * back to the scope label instead of the frequency the AC asks for. The range
 * is appended only when it is genuinely known; projecting it from the
 * remaining occurrence count would invent a date the server never asserted.
 */
export const formatSeriesSummary = (
  currentStart: string,
  currentEnd: string,
  timezone: string,
  series: SeriesCadence,
): string | null => {
  const { endsOn, intervalWeeks, startsOn } = series;
  if (!intervalWeeks) return windowSummary(series);

  let cadence = `Every ${intervalWeeks} weeks on`;
  if (intervalWeeks === 1) cadence = 'Every';
  if (intervalWeeks === 2) cadence = 'Every other';
  const occurrence = `${cadence} ${formatWeekdayTime(
    currentStart,
    timezone,
  )} - ${formatTime(currentEnd, timezone)}`;
  if (!startsOn || !endsOn) return `${occurrence}.`;
  return `${occurrence} from ${formatDate(startsOn)} - ${formatDate(endsOn)}.`;
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const DAYS_PER_WEEK = 7;

/** One occurrence of a series, as the appointment collection carries it. */
export interface SeriesOccurrence {
  endAt: string;
  startAt: string;
}

interface LocalOccurrence {
  /** Local calendar date of the start, YYYY-MM-DD. */
  date: string;
  /** Whole days since the epoch, counted on the local calendar. */
  dayIndex: number;
  /** Local wall-clock end, HH:MM on a 24-hour clock. */
  endTime: string;
  /** Local wall-clock start, HH:MM on a 24-hour clock. */
  time: string;
  /** Local weekday name of the start. */
  weekday: string;
}

/**
 * Constructing an Intl.DateTimeFormat is the expensive half of formatting, and
 * this runs over every occurrence in the series inside a dialog that
 * re-renders on each Ops-note keystroke. One formatter is built per call and
 * reused for every occurrence.
 */
const localClockFormatter = (timezone: string) =>
  new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    hour: '2-digit',
    hourCycle: 'h23',
    minute: '2-digit',
    month: '2-digit',
    timeZone: timezone,
    weekday: 'long',
    year: 'numeric',
  });

const localClock = (iso: string, formatter: Intl.DateTimeFormat) => {
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) return null;
  return formatter.formatToParts(instant);
};

/**
 * MEN-4306 review: every decision this module makes has to be taken in the
 * timezone the summary is rendered in. Reading the calendar date off the ISO
 * string reads it in UTC, so an 8:00pm Tuesday in Los Angeles is a Wednesday,
 * and the summary contradicts itself -- and the timezone-localized
 * cancellation window beside it -- by naming one day and dating another.
 */
const localOccurrence = (
  { endAt, startAt }: SeriesOccurrence,
  formatter: Intl.DateTimeFormat,
): LocalOccurrence | null => {
  const parts = localClock(startAt, formatter);
  const endParts = localClock(endAt, formatter);
  if (!parts || !endParts) return null;
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((candidate) => candidate.type === type)?.value ?? '';
  const endPart = (type: Intl.DateTimeFormatPartTypes) =>
    endParts.find((candidate) => candidate.type === type)?.value ?? '';
  const year = part('year');
  const month = part('month');
  const day = part('day');
  const weekday = part('weekday');
  if (!year || !month || !day || !weekday) return null;
  return {
    date: `${year}-${month}-${day}`,
    // Midnight UTC of the local calendar date. This is a day counter, not an
    // instant: differencing it gives whole calendar days between occurrences,
    // which is what a weekly cadence is actually defined on. Differencing the
    // instants instead makes a DST week 7d +/- 1h, and rounding that back to
    // whole days is what let a shifted occurrence pass as a regular one.
    dayIndex:
      Date.UTC(Number(year), Number(month) - 1, Number(day)) / MS_PER_DAY,
    endTime: `${endPart('hour')}:${endPart('minute')}`,
    time: `${part('hour')}:${part('minute')}`,
    weekday,
  };
};

const toLocalOccurrences = (
  series: SeriesOccurrence[],
  formatter: Intl.DateTimeFormat,
): LocalOccurrence[] | null => {
  const occurrences: LocalOccurrence[] = [];
  for (const entry of series) {
    const occurrence = localOccurrence(entry, formatter);
    // One unreadable occurrence makes the whole set untrustworthy, so assert
    // nothing rather than reasoning from the rows that happened to parse.
    if (!occurrence) return null;
    occurrences.push(occurrence);
  }
  // Both call sites build these lists from the appointment collection, which
  // is not ordered, so order them here rather than depending on every caller.
  return occurrences.sort((left, right) =>
    left.dayIndex === right.dayIndex
      ? left.time.localeCompare(right.time)
      : left.dayIndex - right.dayIndex,
  );
};

export interface SeriesCadenceInput {
  /**
   * The occurrences that will actually be cancelled. These bound the range,
   * because the range answers "what am I about to destroy".
   */
  affected: SeriesOccurrence[];
  /**
   * Every occurrence the tail contains, cancellable or not. These carry the
   * recurrence rule, because the rule is a property of the series and not of
   * whichever rows survived filtering.
   */
  recurrence: SeriesOccurrence[];
}

/**
 * Derives a cadence from the occurrences themselves.
 *
 * The Ops cancel path has no cadence field to read -- CoachCancelSessionOptions
 * carries only counts and a window -- but it does load the series appointments,
 * so the interval is recoverable from consecutive starts.
 *
 * MEN-4306 review: the two inputs are deliberately separate. Deriving the
 * interval from the cancellable rows alone reads a removed occurrence as a
 * change of frequency -- a biweekly tail whose middle occurrence was already
 * cancelled leaves a 28-day gap and announces itself as "Every 4 weeks".
 * Cancelling, unbinding, or passing changes which occurrences are affected; it
 * never changes the recurrence. So the range comes from `affected` and the
 * interval from `recurrence`.
 *
 * Returns no interval unless the recurrence occurrences agree on a local
 * weekday, a local wall-clock start and end, and a constant whole-week gap, so
 * an irregular, hand-edited, time-shifted, or unevenly-lengthed series
 * degrades to the range-only summary rather than asserting a cadence that is
 * not true. The summary sits immediately before a this-and-following
 * cancellation, so an omitted cadence is always preferable to a guessed one.
 *
 * A legitimate DST offset change is still a cadence: the wall-clock time and
 * the local weekday are unchanged across it, and only the UTC instant moves.
 */
export const cadenceFromOccurrences = (
  { affected, recurrence }: SeriesCadenceInput,
  timezone: string,
): SeriesCadence => {
  if (affected.length === 0) return {};
  const formatter = localClockFormatter(timezone);
  const affectedOccurrences = toLocalOccurrences(affected, formatter);
  if (!affectedOccurrences) return {};

  const bounds = {
    endsOn: affectedOccurrences[affectedOccurrences.length - 1]!.date,
    startsOn: affectedOccurrences[0]!.date,
  };

  const occurrences = toLocalOccurrences(recurrence, formatter);
  if (!occurrences || occurrences.length < 2) return bounds;
  const first = occurrences[0]!;

  // A cadence claims the first occurrence's weekday and its whole wall-clock
  // window -- start and end both appear in the summary -- describe the entire
  // tail. Check that before claiming it.
  const consistentClock = occurrences.every(
    (occurrence) =>
      occurrence.time === first.time &&
      occurrence.endTime === first.endTime &&
      occurrence.weekday === first.weekday,
  );
  if (!consistentClock) return bounds;

  const intervalDays = occurrences[1]!.dayIndex - first.dayIndex;
  if (intervalDays <= 0 || intervalDays % DAYS_PER_WEEK !== 0) return bounds;
  for (let index = 1; index < occurrences.length - 1; index += 1) {
    const gap = occurrences[index + 1]!.dayIndex - occurrences[index]!.dayIndex;
    if (gap !== intervalDays) return bounds;
  }
  return { ...bounds, intervalWeeks: intervalDays / DAYS_PER_WEEK };
};
